"use client";

/**
 * Presentational lesson player + rating UI. Pair with useLessonSession.
 */

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  formatDurationMs,
  formatLessonTitle,
  formatPlaybackTime,
  SELF_RATING_OPTIONS,
  selfRatingLabel,
} from "./lessonCardState";
import type { UseLessonSessionResult } from "./useLessonSession";

interface LessonPlayerProps {
  session: UseLessonSessionResult;
  /** Heading label. Defaults to "Today's lesson". */
  heading?: string;
}

export default function LessonPlayer({
  session,
  heading = "Today's lesson",
}: LessonPlayerProps) {
  const {
    lesson,
    error,
    busy,
    phase,
    justRated,
    generatingLocked,
    audioRef,
    audioUrl,
    playing,
    currentTime,
    duration,
    handleGenerate,
    handleRate,
    togglePlay,
    seek,
    finishForRating,
  } = session;

  return (
    <section
      aria-label={heading}
      className="rounded-md border border-app-border bg-app-surface p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-app-muted">
            {heading}
          </p>
          {phase === "loading" ? (
            <h2 className="mt-1 text-lg font-semibold text-app-text">
              Loading…
            </h2>
          ) : lesson && phase !== "empty" ? (
            <>
              <h2 className="mt-1 truncate text-lg font-semibold text-app-text">
                {formatLessonTitle(lesson)}
              </h2>
              <p className="mt-1 text-sm text-app-body">
                {formatDurationMs(lesson.duration_ms)}
                {lesson.new_count != null || lesson.review_count != null ? (
                  <>
                    {" · "}
                    {lesson.new_count ?? 0} new
                    {" · "}
                    {lesson.review_count ?? 0} review
                  </>
                ) : null}
              </p>
            </>
          ) : (
            <h2 className="mt-1 text-lg font-semibold text-app-text">
              No lesson yet
            </h2>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={generatingLocked}
          onClick={() => void handleGenerate()}
          className="shrink-0 border-app-border text-app-body hover:bg-app-tinted hover:text-app-text"
        >
          {phase === "empty" ? "Generate my first lesson" : "New lesson"}
        </Button>
      </div>

      {error && (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <div className="mt-4">
        {phase === "loading" && (
          <QuietStatus>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading lesson…
          </QuietStatus>
        )}

        {phase === "empty" && (
          <p className="text-sm text-app-body">
            Generate a short audio drill from your recent reading. No autoplay —
            you control when it plays.
          </p>
        )}

        {phase === "generating" && (
          <QuietStatus>
            <span className="h-2 w-2 animate-pulse rounded-full bg-app-muted" />
            Generating your lesson… this usually takes a minute.
          </QuietStatus>
        )}

        {phase === "failed" && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-destructive">
              Generation failed
              {lesson?.title?.includes("skipped")
                ? " — not enough material yet. Read a bit more, then try again."
                : ". You can retry."}
            </p>
            <Button
              type="button"
              size="sm"
              disabled={generatingLocked}
              onClick={() => void handleGenerate()}
            >
              Retry
            </Button>
          </div>
        )}

        {(phase === "ready" || phase === "rating" || phase === "rated") &&
          lesson && (
            <div className="space-y-3">
              {audioUrl && (
                <>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio ref={audioRef} preload="metadata" src={audioUrl} />
                  <div className="flex items-center gap-2 rounded-full border border-app-border bg-app-surface px-2 py-1.5 shadow-sm">
                    <button
                      type="button"
                      onClick={togglePlay}
                      aria-label={playing ? "Pause" : "Play"}
                      title={playing ? "Pause" : "Play"}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-app-accent text-app-on-accent transition-colors hover:opacity-90"
                    >
                      {playing ? <PauseIcon /> : <PlayIcon />}
                    </button>

                    <span className="w-10 shrink-0 text-right text-xs tabular-nums text-app-body">
                      {formatPlaybackTime(currentTime)}
                    </span>

                    <input
                      type="range"
                      min={0}
                      max={1000}
                      value={
                        duration > 0
                          ? Math.round((currentTime / duration) * 1000)
                          : 0
                      }
                      onChange={(e) => seek(Number(e.target.value) / 1000)}
                      aria-label="Seek"
                      className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-app-tinted accent-app-accent"
                    />

                    <span className="w-10 shrink-0 text-xs tabular-nums text-app-body">
                      {formatPlaybackTime(
                        duration || (lesson.duration_ms ?? 0) / 1000,
                      )}
                    </span>
                  </div>
                </>
              )}

              {phase === "ready" && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={finishForRating}
                    className="border-app-border text-app-body hover:bg-app-tinted hover:text-app-text"
                  >
                    Finish
                  </Button>
                </div>
              )}

              {phase === "rating" && (
                <div className="rounded-md border border-app-border bg-app-tinted px-3 py-3">
                  <p className="text-sm text-app-text">
                    How many did you get right?
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {SELF_RATING_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void handleRate(opt.value)}
                        className="border-app-border text-app-body hover:bg-app-surface hover:text-app-text"
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {phase === "rated" && (
                <p className="text-sm text-app-body">
                  Rated{" "}
                  <span className="font-medium text-app-text">
                    {selfRatingLabel(lesson.self_rating) ?? "done"}
                  </span>
                  {justRated ? " — saved." : "."}
                </p>
              )}
            </div>
          )}
      </div>
    </section>
  );
}

function QuietStatus({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-app-body">{children}</div>
  );
}

function PlayIcon() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  );
}
